import { Info, AlertTriangle, Lightbulb, ChevronDown, PlayCircle } from "lucide-react";
import { useState } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// BLOCK SCHEMA (contract between the SEO dashboard and the blog page)
//
//  heading   { type:'heading',  level: 2|3, content }
//  paragraph { type:'paragraph', content }                 // **bold** allowed
//  list      { type:'list', style:'bullet'|'numbered'|'checklist', items:[] }
//  image     { type:'image', src, alt, caption?, width:'25'|'50'|'75'|'100', align:'left'|'right'|'center' }
//  quote     { type:'quote', content, attribution? }
//  callout   { type:'callout', variant:'tip'|'warning'|'info', content }
//  cta       { type:'cta', heading, subtext, buttonText, buttonLink }
//  faq       { type:'faq', title?, items:[{ q, a }] }
//  video     { type:'video', provider:'youtube'|'vimeo', videoId, caption? }
//  table     { type:'table', caption?, headers:[string], rows:[[string]] }
//  divider   { type:'divider' }
//
// LAYOUT MODEL — Word-style wrap: blocks are siblings in one flow container,
// so an `image` with align:left|right can FLOAT and following paragraphs wrap
// around it. "Break" blocks add .bl-break (clear:both) to end the wrap.
// ─────────────────────────────────────────────────────────────────────────────

// Minimal inline markdown — just **bold** and *italic*.
export function renderInline(text = "") {
  let html = String(text).replace(/\*\*(.+?)\*\*/g, '<strong class="bl-fw-600">$1</strong>');
  html = html.replace(/(?<!\*)\*(?!\*)([^*]+)\*(?!\*)/g, "<em>$1</em>");
  return { __html: html };
}

// Turn heading text into a stable id. Used only internally for TOC
// scroll-to-element targeting — never written to the URL.
function slugifyHeading(text = "") {
  return String(text)
    .toLowerCase()
    .replace(/\*+/g, "")
    .replace(/<[^>]+>/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "") || "section";
}

// Flat table of contents (H2 + H3) with de-duplicated anchor ids.
// Same iteration order and de-dup rule as BlockRenderer below, so the ids
// in the TOC match the ids injected onto the rendered headings.
export function extractToc(blocks = []) {
  const seen = new Map();
  const toc = [];
  blocks.forEach((b) => {
    if (b.type !== "heading") return;
    const level = b.level === 3 ? 3 : 2;
    const base = slugifyHeading(b.content);
    const n = seen.get(base) || 0;
    seen.set(base, n + 1);
    const id = n ? `${base}-${n}` : base;
    toc.push({
      id,
      level,
      content: String(b.content || "").replace(/\*+/g, ""),
    });
  });
  return toc;
}

function HeadingBlock({ level = 2, content, _anchor }) {
  const Tag = level === 3 ? "h3" : "h2";
  const cls = level === 3 ? "bl-article-h3 bl-break" : "bl-article-h2 bl-break";
  return <Tag id={_anchor} className={cls} dangerouslySetInnerHTML={renderInline(content)} />;
}

function ParagraphBlock({ content }) {
  // No wrapper — this block must wrap around floats.
  return <p className="bl-muted bl-article-p" dangerouslySetInnerHTML={renderInline(content)} />;
}

function ListBlock({ style = "bullet", items = [] }) {
  if (style === "checklist") {
    return (
      <ul className="bl-article-list bl-article-list--check bl-break">
        {items.map((item, i) => (
          <li key={i} dangerouslySetInnerHTML={renderInline(item)} />
        ))}
      </ul>
    );
  }
  const Tag = style === "numbered" ? "ol" : "ul";
  const cls = (style === "numbered"
    ? "bl-article-list bl-article-list--numbered"
    : "bl-article-list bl-article-list--bullet") + " bl-break";
  return (
    <Tag className={cls}>
      {items.map((item, i) => (
        <li key={i} dangerouslySetInnerHTML={renderInline(item)} />
      ))}
    </Tag>
  );
}

function ImageBlock({ src, alt = "", caption, width = "100", align = "center" }) {
  if (!src) return null;
  const wrapClass =
    align === "left" ? "bl-article-img--left" :
    align === "right" ? "bl-article-img--right" :
    "bl-article-img--center bl-break";
  return (
    <figure className={`bl-article-img ${wrapClass}`} style={{ width: `${width}%` }}>
      <img src={src} alt={alt} className="bl-article-img__el" />
      {caption && <figcaption className="bl-article-img__caption">{caption}</figcaption>}
    </figure>
  );
}

function QuoteBlock({ content, attribution }) {
  return (
    <blockquote className="bl-article-quote bl-break">
      <p dangerouslySetInnerHTML={renderInline(content)} />
      {attribution && <cite className="bl-article-quote__cite">— {attribution}</cite>}
    </blockquote>
  );
}

const calloutIcon = { tip: Lightbulb, warning: AlertTriangle, info: Info };
function CalloutBlock({ variant = "tip", content }) {
  const Icon = calloutIcon[variant] || Lightbulb;
  return (
    <div className={`bl-callout bl-callout--${variant} bl-break`}>
      <Icon className="bl-callout__icon" />
      <p dangerouslySetInnerHTML={renderInline(content)} />
    </div>
  );
}

function CtaBlock({ heading, subtext, buttonText, buttonLink }) {
  return (
    <div className="bl-cta-box bl-cta-box--inline bl-break">
      {heading && <h2 className="bl-fluid-2xl bl-fw-800 bl-cta-title">{heading}</h2>}
      {subtext && <p className="bl-cta-sub">{subtext}</p>}
      {buttonText && (
        <a href={buttonLink || "#"} className="bl-btn-primary bl-cta-btn">{buttonText}</a>
      )}
    </div>
  );
}

function FaqBlock({ title, items = [] }) {
  const [open, setOpen] = useState(-1);
  const valid = items.filter(it => it && (it.q || it.a));
  if (valid.length === 0) return null;
  return (
    <section className="bl-article-faq bl-break" aria-label={title || "FAQ"}>
      {title && <h3 className="bl-article-faq__title">{title}</h3>}
      <ul className="bl-article-faq__list">
        {valid.map((it, i) => {
          const isOpen = open === i;
          return (
            <li key={i} className={`bl-article-faq__item${isOpen ? " is-open" : ""}`}>
              <button
                type="button"
                className="bl-article-faq__q"
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? -1 : i)}
              >
                <span dangerouslySetInnerHTML={renderInline(it.q || "")} />
                <ChevronDown className="bl-article-faq__chev" />
              </button>
              {isOpen && (
                <div className="bl-article-faq__a" dangerouslySetInnerHTML={renderInline(it.a || "")} />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function VideoBlock({ provider = "youtube", videoId, caption }) {
  if (!videoId) {
    return (
      <div className="bl-article-video bl-article-video--empty bl-break">
        <PlayCircle className="bl-article-video__icon" />
        <span>Video ID missing</span>
      </div>
    );
  }
  const src = provider === "vimeo"
    ? `https://player.vimeo.com/video/${videoId}`
    : `https://www.youtube.com/embed/${videoId}`;
  return (
    <figure className="bl-article-video bl-break">
      <div className="bl-article-video__frame">
        <iframe
          src={src}
          title={caption || "Embedded video"}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      {caption && <figcaption className="bl-article-img__caption">{caption}</figcaption>}
    </figure>
  );
}

// Dynamic table — headers optional, any number of rows/columns.
function TableBlock({ headers = [], rows = [], caption }) {
  const hasHeaders = headers.some(h => (h || "").toString().trim() !== "");
  const cleanRows = rows.filter(r => Array.isArray(r) && r.some(c => (c || "").toString().trim() !== ""));
  if (!hasHeaders && cleanRows.length === 0) return null;
  return (
    <figure className="bl-article-table bl-break">
      <div className="bl-article-table__scroll">
        <table className="bl-article-table__el">
          {hasHeaders && (
            <thead>
              <tr>
                {headers.map((h, i) => (
                  <th key={i} scope="col" dangerouslySetInnerHTML={renderInline(h || "")} />
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {cleanRows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci} dangerouslySetInnerHTML={renderInline(cell || "")} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {caption && <figcaption className="bl-article-img__caption">{caption}</figcaption>}
    </figure>
  );
}

function DividerBlock() {
  return <hr className="bl-article-divider bl-break" />;
}

// Adding a block type = one component + one entry here.
const blockComponents = {
  heading: HeadingBlock,
  paragraph: ParagraphBlock,
  list: ListBlock,
  image: ImageBlock,
  quote: QuoteBlock,
  callout: CalloutBlock,
  cta: CtaBlock,
  faq: FaqBlock,
  video: VideoBlock,
  table: TableBlock,
  divider: DividerBlock,
};

// No per-block wrapper div: siblings in one flow container is what enables
// Word-style text wrap around floated images. Injects TOC anchor ids onto
// heading blocks so the sticky TOC on the detail page can link to them.
export default function BlockRenderer({ blocks = [] }) {
  const toc = extractToc(blocks);
  let headingIdx = 0;
  return (
    <div className="bl-blocks-flow">
      {blocks.map((block, i) => {
        const Component = blockComponents[block.type];
        if (!Component) return null;
        const extra =
          block.type === "heading"
            ? { _anchor: toc[headingIdx++]?.id }
            : {};
        return <Component key={block._id || i} {...block} {...extra} />;
      })}
    </div>
  );
}

export { blockComponents };